import Cookies from 'js-cookie';
import { getCookieDomain } from './cookieDomain';

export interface CouponCookieData {
  code: string;
  bannerText?: string;
  /** GWA entrypoint attribution (e.g. `fi_nano_registration`), forwarded into the purchase. */
  source?: string;
  /** Originating rescue's GWA program id, forwarded into the purchase for redemption attribution. */
  rescueGwaProgramId?: number;
}

export function removeCookie(name: string) {
  Cookies.remove(name, { domain: getCookieDomain() });
}

/**
 * A failed auto-apply against an empty cart says nothing about the code. The server deliberately
 * skips its eligible-items check when the cart is empty, so the client falls through to a recurly.js
 * lookup instead, and that lookup cannot resolve an internal partner coupon: it reports `not-found`
 * for a perfectly good code. Clearing on that would strand a shopper who has no promo field to retry
 * in, which is every rescue adopter - their alias is a fi-side identifier that only resolves
 * server-side and has no recurly coupon behind it.
 *
 * An entitled shopper is held to the same rule at any cart size, for the same reason: their code is
 * an alias, so `not-found` is what the client reports for *any* response that carries no discount,
 * including a server-side fault that has nothing to do with the code. Deleting the cookie on that
 * turns one bad response into a lost gift, since the alias only exists in the cookie.
 */
export function shouldClearCouponCookieOnFailure(cartItemCount: number, hasEntitlement: boolean): boolean {
  return cartItemCount > 0 && !hasEntitlement;
}

export const COUPON_COOKIE_NAME = 'coupon_data';
export const COUPON_COOKIE_EXPIRATION_HOURS = 72;

/**
 * Separate cookie holding the signed GWA entitlement (program-id context) from the offer-token
 * exchange or the rescue slug landing. Kept out of `coupon_data` so the coupon-apply machinery can't
 * clobber it (the slug flow has no code to apply), and forwarded on pricing/validate/purchase.
 */
export const GWA_ENTITLEMENT_COOKIE_NAME = 'gwa_entitlement';

/**
 * Signed GwP partner entitlement (program-id context) from the partner slug landing. Same shape and
 * lifecycle as `gwa_entitlement`, kept separate because the server resolves the two token types
 * against different programs. Forwarded on pricing/validate/purchase as `gwpToken`.
 */
export const GWP_ENTITLEMENT_COOKIE_NAME = 'gwp_entitlement';
